import { useEffect, useState } from 'preact/hooks';
import type { MutableRef } from 'preact/hooks';
import { capturePage } from '../capture';
import {
  LOCATION_CHANGE_EVENT,
  type LocalComment,
  addLocalComment,
  deleteLocalComment,
  loadCommentsForPage,
  mergeRemoteComments,
  pageKey,
  updateLocalComment,
} from '../storage';
import {
  ApiError,
  deleteComment as apiDeleteComment,
  listComments,
  submitComment,
  toggleReaction as apiToggleReaction,
  updateComment as apiUpdateComment,
} from '../api';
import { deriveOffsetPct } from '../lib/comments';
import { dashboardOriginFrom } from '../lib/origin';
import type { DraftPin } from '../lib/types';
import type { HostAdapter } from '@pinmerge/shared';
import type { ReviewerCache } from '../identity';
import { t } from '../i18n';

interface ThreadsConfig {
  token: string;
  apiUrl: string;
  demoMode: boolean;
  /** Prototype-version slug carried on every submit/list call. Resolved
   *  server-side to a prototype_versions.id; the server falls back to the
   *  project default when null. */
  versionSlug: string | null;
}

interface UseThreadsArgs {
  config: ThreadsConfig;
  ownTokenRef: MutableRef<string>;
  reviewerName: string;
  /** Current reviewer session — used to seed the avatar on optimistically
   *  rendered comments before the server round-trip completes. */
  reviewer: ReviewerCache | null;
  onSessionInvalid: () => void;
  setError: (msg: string | null) => void;
  /** Host-application adapter. When present, its resolveRoute()/resolveSource()
   *  feed the route bucket + source anchor into submit/list. A null-adapter
   *  (resolveRoute→null, resolveSource→null) leaves every field null, so the
   *  behaviour is identical to the script-tag channel (I3). */
  adapter?: HostAdapter;
}

/** Resolved prototype-version metadata returned by the server alongside the
 *  comments list. Used by the FAB-toolbar badge to show the reviewer which
 *  iteration they're looking at. NULL when the project has no versions
 *  configured (or the GET hasn't completed yet). */
export interface ActiveVersion {
  id: string;
  slug: string;
  label: string;
}

export interface UseThreadsResult {
  comments: LocalComment[];
  projectSlug: string | null;
  activeVersion: ActiveVersion | null;
  linkCopiedFor: string | null;
  reload: () => void;
  saveDraft: (draft: DraftPin, text: string) => string;
  saveEdit: (localId: string, text: string) => void;
  remove: (localId: string) => void;
  submitReply: (parent: LocalComment, replyText: string) => Promise<void>;
  saveReplyEdit: (replyId: string, text: string) => Promise<void>;
  removeReply: (replyId: string) => void;
  toggleReaction: (commentId: string, remoteId: string, emoji: string) => Promise<void>;
  copyPermalink: (remoteId: string) => Promise<void>;
}

export function useThreads({
  config,
  ownTokenRef,
  reviewerName,
  reviewer,
  onSessionInvalid,
  setError,
  adapter,
}: UseThreadsArgs): UseThreadsResult {
  const [comments, setComments] = useState<LocalComment[]>(() => loadCommentsForPage());
  const [projectSlug, setProjectSlug] = useState<string | null>(null);
  const [activeVersion, setActiveVersion] = useState<ActiveVersion | null>(null);
  const [linkCopiedFor, setLinkCopiedFor] = useState<string | null>(null);

  function reload() {
    setComments(loadCommentsForPage());
  }

  // Refresh on mount + visibility/focus + SPA route change. Auth / URL-allow
  // failures are common during development — log and move on instead of
  // blowing up. The pinmerge:locationchange event is dispatched by main.ts's
  // history-patch and by setPageKey(); both should re-bucket the visible pins.
  useEffect(() => {
    if (config.demoMode) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const result = await listComments(config.apiUrl, {
          token: config.token,
          url: window.location.href,
          creatorToken: ownTokenRef.current,
          versionSlug: config.versionSlug,
          // Route-aware bucket from the adapter (null-adapter → null → the
          // server falls back to pageKeyOf(url), i.e. unchanged behaviour).
          pageKey: adapter?.resolveRoute?.()?.pattern ?? null,
        });
        if (cancelled) return;
        setProjectSlug(result.project?.slug ?? null);
        setActiveVersion(result.active_version ?? null);
        const merged = mergeRemoteComments(
          result.comments.map((r) => ({
            id: r.id,
            authorName: r.author_name,
            text: r.comment_text,
            docX: r.click_xy?.x ?? 0,
            docY: r.click_xy?.y ?? 0,
            clickOffsetPct: r.click_offset_pct ?? deriveOffsetPct(r.click_xy, r.element_rect),
            elementSelector: r.element_selector,
            elementHtml: r.element_html,
            elementRect: r.element_rect,
            viewport: { w: r.viewport.w, h: r.viewport.h },
            createdAt: r.created_at,
            updatedAt: r.updated_at,
            mine: r.mine,
            status: r.status,
            screenshotPath: r.screenshot_path,
            thumbnailPath: r.thumbnail_path,
            thumbnailUrl: r.thumbnail_url,
            avatar: { id: r.reviewer?.avatar_id ?? null, bg: r.reviewer?.avatar_bg ?? null },
            reactions: r.reactions ?? [],
            parentId: r.parent_id,
          })),
        );
        setComments(merged);
      } catch (err) {
        console.warn('[pinmerge] list comments failed', err);
      }
    };
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onLocationChange = () => {
      // Drop the locally-cached list immediately so the old page's pins don't
      // flash on the new page during the network round-trip. mergeRemoteComments
      // re-seats the fetched rows into the new bucket on completion.
      setComments(loadCommentsForPage());
      refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    window.addEventListener(LOCATION_CHANGE_EVENT, onLocationChange);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(LOCATION_CHANGE_EVENT, onLocationChange);
    };
  }, [config.apiUrl, config.token, config.demoMode, config.versionSlug]);

  function saveDraft(draft: DraftPin, text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return '';
    const now = new Date().toISOString();
    const comment: LocalComment = {
      id: crypto.randomUUID(),
      pageKey: pageKey(),
      authorName: reviewerName,
      text: trimmed,
      docX: draft.docX,
      docY: draft.docY,
      clickOffsetPct: draft.clickOffsetPct,
      elementSelector: draft.elementSelector,
      elementHtml: draft.elementHtml,
      elementRect: draft.rect,
      viewport: draft.viewport,
      createdAt: now,
      updatedAt: now,
      creatorToken: ownTokenRef.current,
      status: 'open',
      // Seed the avatar from the session so the pin renders the right face
      // immediately — without this it shows initials until the next GET.
      ...(reviewer
        ? { avatar: { id: reviewer.avatar_id ?? null, bg: reviewer.avatar_bg ?? null } }
        : {}),
    };
    addLocalComment(comment);
    reload();

    if (!config.demoMode) {
      // Background sync — local persistence is the source of truth for the
      // reviewer's UI; the server is for the project owner. Screenshot is
      // best-effort: if html2canvas throws (cross-origin image, OOM on iOS,
      // unsupported CSS) we still submit the metadata.
      syncToApi(comment).catch((err) => {
        console.error('[pinmerge] sync failed (local copy is safe)', err);
      });
    }
    return comment.id;
  }

  async function syncToApi(comment: LocalComment): Promise<void> {
    let screenshot: Blob | null = null;
    let thumbnail: Blob | null = null;
    // Source anchor from the adapter's resolveSource(), captured alongside the
    // screenshot. Stays null when there's no target, capture failed, or the
    // adapter resolved null (incl. the null-adapter).
    let source: {
      source_file: string | null;
      source_line: number | null;
      source_column: number | null;
      component_name: string | null;
    } | null = null;

    const target = comment.elementSelector
      ? (document.querySelector(comment.elementSelector) as HTMLElement | null)
      : null;

    if (target) {
      try {
        const click = { x: comment.docX - window.scrollX, y: comment.docY - window.scrollY };
        const captured = await capturePage(target, click, {
          resolveSource: adapter?.resolveSource
            ? (el) => adapter.resolveSource!(el)
            : undefined,
        });
        screenshot = captured.screenshotBlob;
        thumbnail = captured.thumbnailBlob;
        source = {
          source_file: captured.source_file,
          source_line: captured.source_line,
          source_column: captured.source_column,
          component_name: captured.component_name,
        };
      } catch (err) {
        console.warn('[pinmerge] screenshot capture failed; submitting without it', err);
      }
    }

    // ── Naht C: camelCase ↔ snake_case boundary ─────────────────────────
    // storage.ts (LocalComment/RemoteSnapshot) uses camelCase (routePattern…),
    // while the API body + DB columns use snake_case (route_pattern…). This is
    // the single translation point for the route anchor on the submit path:
    // the adapter's RouteInfo { pattern, params } → snake_case page_key /
    // route_pattern / route_params. `pattern` fills BOTH page_key (the opaque
    // bucket the server stores + filters on) and route_pattern (display). The
    // source_* fields already come back snake_case from capturePage, so they
    // map 1:1. With a null-adapter every value here is null → the body matches
    // the script-tag channel (I3).
    const route = adapter?.resolveRoute?.() ?? null;

    const result = await submitComment(config.apiUrl, {
      token: config.token,
      creatorToken: ownTokenRef.current,
      url: window.location.href,
      page_title: document.title,
      comment_text: comment.text,
      author_name: comment.authorName || reviewerName,
      viewport: {
        w: comment.viewport.w,
        h: comment.viewport.h,
        dpr: window.devicePixelRatio || 1,
      },
      click_xy: { x: comment.docX, y: comment.docY },
      click_offset_pct: comment.clickOffsetPct ?? null,
      element_rect: comment.elementRect,
      element_selector: comment.elementSelector,
      element_html: comment.elementHtml,
      screenshot,
      thumbnail,
      version_slug: config.versionSlug,
      page_key: route?.pattern ?? null,
      route_pattern: route?.pattern ?? null,
      route_params: route?.params ?? null,
      source_file: source?.source_file ?? null,
      source_line: source?.source_line ?? null,
      source_column: source?.source_column ?? null,
      component_name: source?.component_name ?? null,
    });

    updateLocalComment(comment.id, {
      remoteId: result.id,
      ...(result.screenshot_path ? { screenshotPath: result.screenshot_path } : {}),
      ...(result.thumbnail_path ? { thumbnailPath: result.thumbnail_path } : {}),
      ...(result.thumbnail_url ? { thumbnailUrl: result.thumbnail_url } : {}),
      ...(result.reviewer
        ? { avatar: { id: result.reviewer.avatar_id, bg: result.reviewer.avatar_bg } }
        : {}),
    });
    reload();
    console.info(
      '[pinmerge] synced:',
      result.id,
      result.screenshot_path ? `(with screenshot ${result.screenshot_path})` : '(no screenshot)',
    );
  }

  function saveEdit(localId: string, text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const updated = updateLocalComment(localId, { text: trimmed });
    reload();

    if (!config.demoMode && updated?.remoteId) {
      apiUpdateComment(config.apiUrl, {
        id: updated.remoteId,
        token: config.token,
        creatorToken: ownTokenRef.current,
        url: window.location.href,
        comment_text: trimmed,
      }).catch((err) => {
        console.error('[pinmerge] remote edit failed', err);
        if (err instanceof ApiError && err.status === 403) {
          setError(t('errors.edit-own-only'));
        }
      });
    }
  }

  function remove(localId: string) {
    const target = comments.find((c) => c.id === localId);
    deleteLocalComment(localId);
    reload();

    if (!config.demoMode && target?.remoteId) {
      apiDeleteComment(config.apiUrl, {
        id: target.remoteId,
        token: config.token,
        creatorToken: ownTokenRef.current,
        url: window.location.href,
      }).catch((err) => {
        console.error('[pinmerge] remote delete failed', err);
      });
    }
  }

  async function submitReply(parent: LocalComment, replyText: string) {
    if (!parent.remoteId) return;
    const trimmed = replyText.trim();
    if (!trimmed) return;
    const now = new Date().toISOString();
    const reply: LocalComment = {
      id: crypto.randomUUID(),
      pageKey: pageKey(),
      authorName: reviewerName,
      text: trimmed,
      docX: 0,
      docY: 0,
      elementSelector: null,
      elementHtml: null,
      elementRect: null,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      createdAt: now,
      updatedAt: now,
      creatorToken: ownTokenRef.current,
      status: 'open',
      parentId: parent.id,
      reactions: [],
      ...(reviewer
        ? { avatar: { id: reviewer.avatar_id ?? null, bg: reviewer.avatar_bg ?? null } }
        : {}),
    };
    addLocalComment(reply);
    reload();

    if (config.demoMode) return;
    try {
      const result = await submitComment(config.apiUrl, {
        token: config.token,
        creatorToken: ownTokenRef.current,
        url: window.location.href,
        page_title: document.title,
        comment_text: trimmed,
        author_name: reviewerName,
        viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
        click_xy: null,
        click_offset_pct: null,
        element_rect: null,
        element_selector: null,
        element_html: null,
        parent_id: parent.remoteId,
        version_slug: config.versionSlug,
      });
      updateLocalComment(reply.id, {
        remoteId: result.id,
        ...(result.reviewer
          ? { avatar: { id: result.reviewer.avatar_id, bg: result.reviewer.avatar_bg } }
          : {}),
      });
      reload();
    } catch (err) {
      console.error('[pinmerge] reply sync failed (local copy is safe)', err);
    }
  }

  async function saveReplyEdit(replyId: string, text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const updated = updateLocalComment(replyId, { text: trimmed });
    reload();
    if (!config.demoMode && updated?.remoteId) {
      try {
        await apiUpdateComment(config.apiUrl, {
          id: updated.remoteId,
          token: config.token,
          creatorToken: ownTokenRef.current,
          url: window.location.href,
          comment_text: trimmed,
        });
      } catch (err) {
        console.error('[pinmerge] reply edit sync failed', err);
      }
    }
  }

  function removeReply(replyId: string) {
    const reply = comments.find((c) => c.id === replyId);
    deleteLocalComment(replyId);
    reload();
    if (!config.demoMode && reply?.remoteId) {
      apiDeleteComment(config.apiUrl, {
        id: reply.remoteId,
        token: config.token,
        creatorToken: ownTokenRef.current,
        url: window.location.href,
      }).catch((err) => console.error('[pinmerge] reply delete failed', err));
    }
  }

  async function toggleReaction(commentId: string, remoteId: string, emoji: string) {
    if (config.demoMode) return;
    // Optimistic update — server-side toggle has 3 outcomes, mirror them
    // locally and revert on failure. Keeps the picker snappy on slow links.
    const prev = comments;
    setComments((curr) =>
      curr.map((c) => (c.id === commentId ? optimisticToggle(c, emoji) : c)),
    );
    try {
      await apiToggleReaction(config.apiUrl, {
        id: remoteId,
        token: config.token,
        creatorToken: ownTokenRef.current,
        url: window.location.href,
        emoji,
      });
    } catch (err) {
      console.warn('[pinmerge] reaction toggle failed', err);
      setComments(prev);
      if (err instanceof ApiError && err.status === 403 && err.code === 'registration_required') {
        // Cookie says we're signed in, server disagrees — clear stale cache.
        onSessionInvalid();
      }
    }
  }

  async function copyPermalink(remoteId: string) {
    if (!projectSlug) return;
    const url = `${dashboardOriginFrom(config.apiUrl)}/projects/${projectSlug}?pinmerge_comment=${remoteId}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopiedFor(remoteId);
      setTimeout(() => setLinkCopiedFor((v) => (v === remoteId ? null : v)), 1500);
    } catch (err) {
      console.warn('[pinmerge] clipboard write failed', err);
    }
  }

  return {
    comments,
    projectSlug,
    activeVersion,
    linkCopiedFor,
    reload,
    saveDraft,
    saveEdit,
    remove,
    submitReply,
    saveReplyEdit,
    removeReply,
    toggleReaction,
    copyPermalink,
  };
}

// Mirrors the server-side toggle_reaction RPC: same emoji removes, different
// replaces, new emoji adds. Used for optimistic UI updates while the network
// call is in flight.
function optimisticToggle(c: LocalComment, emoji: string): LocalComment {
  const reactions = (c.reactions ?? []).map((r) => ({ ...r }));
  const myCurrent = reactions.find((r) => r.mine);
  if (myCurrent && myCurrent.emoji === emoji) {
    myCurrent.count -= 1;
    myCurrent.mine = false;
  } else {
    if (myCurrent) {
      myCurrent.count -= 1;
      myCurrent.mine = false;
    }
    const target = reactions.find((r) => r.emoji === emoji);
    if (target) {
      target.count += 1;
      target.mine = true;
    } else {
      reactions.push({ emoji, count: 1, mine: true });
    }
  }
  return { ...c, reactions: reactions.filter((r) => r.count > 0) };
}
